/**
 * Microcourse Studio — PlaybackBar
 * 播放控制栏 UI：上一步 / 播放暂停 / 下一步 三个导航按钮（居中），两侧为状态与进度信息
 */

import React from 'react'
import { Play, Pause, SkipBack, SkipForward, Volume2 } from 'lucide-react'
import type { AutoplayStatus } from './useAutoplay'

export interface PlaybackBarProps {
  status: AutoplayStatus
  currentSlideIndex: number
  totalSlides: number
  currentSegIndex: number
  totalSegments: number
  statusText: string
  speaking: boolean
  onPlay: () => void
  onPause: () => void
  onResume: () => void
  onPrev: () => void
  onNext: () => void
  /** 上一步是否禁用（缺省按 slide 边界判断） */
  prevDisabled?: boolean
  /** 下一步是否禁用（缺省按 slide 边界判断） */
  nextDisabled?: boolean
  className?: string
}

export function PlaybackBar({
  status,
  currentSlideIndex,
  totalSlides,
  currentSegIndex,
  totalSegments,
  statusText,
  speaking,
  onPlay,
  onPause,
  onResume,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  className = '',
}: PlaybackBarProps) {
  const isPlaying = status === 'playing'
  const isPaused = status === 'paused'
  const isActive = isPlaying || isPaused

  const progressPercent = totalSlides > 0 ? ((currentSlideIndex + 1) / totalSlides) * 100 : 0

  const isPrevDisabled = prevDisabled ?? currentSlideIndex <= 0
  const isNextDisabled = nextDisabled ?? currentSlideIndex >= totalSlides - 1

  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 border-t border-slate-200 bg-white/95 backdrop-blur text-sm ${className}`}
    >
      {/* 左侧：状态信息 */}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        {speaking && <Volume2 className="w-4 h-4 text-blue-500 animate-pulse flex-shrink-0" />}
        {isActive && totalSegments > 0 && (
          <span className="text-xs text-slate-400 tabular-nums flex-shrink-0">
            段 {Math.max(0, currentSegIndex + 1)}/{totalSegments}
          </span>
        )}
        {statusText && (
          <span className="text-xs text-slate-400 truncate hidden lg:inline">{statusText}</span>
        )}
      </div>

      {/* 中间：三个导航按钮（上一步 / 播放暂停 / 下一步） */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* 上一步 */}
        <button
          onClick={onPrev}
          disabled={isPrevDisabled}
          className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="上一步"
        >
          <SkipBack className="w-4 h-4" />
        </button>

        {/* 播放 / 暂停 / 继续 */}
        {isPlaying ? (
          <button
            onClick={onPause}
            className="p-2 rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-colors shadow-sm"
            title="暂停"
          >
            <Pause className="w-4 h-4" />
          </button>
        ) : isPaused ? (
          <button
            onClick={onResume}
            className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
            title="继续"
          >
            <Play className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onPlay}
            className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
            title="语音讲解播放"
          >
            <Play className="w-4 h-4" />
          </button>
        )}

        {/* 下一步 */}
        <button
          onClick={onNext}
          disabled={isNextDisabled}
          className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="下一步"
        >
          <SkipForward className="w-4 h-4" />
        </button>
      </div>

      {/* 右侧：进度与页码 */}
      <div className="flex-1 flex items-center justify-end gap-3 min-w-0">
        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">
          {currentSlideIndex + 1} / {totalSlides}
        </span>
      </div>
    </div>
  )
}
